<template>
  <Screen
    v-if="isUserCreatorInGroup && style"
    center
    :header-title="styleName"
    class="project-style-create"
  >
    <ElPopover
      v-if="canUserAccessFeatureStyleDataIntegration"
      slot="header-right"
      v-model="showAssetManager"
      width="300"
      placement="bottom"
      popper-class="project-style-create__asset-browser-popup"
    >
      <StitchAssetBrowser
        v-if="showAssetManager"
        v-click-outside="closeAssetManager"
        asset-type="style"
        output-type="asset"
        class="project-style-create__asset-browser"
        @select-asset="handleSelectedStyle"
      />
      <ElButton
        slot="reference"
        plain
        size="mini"
        class="project-style-create__asset-browser-button"
      >
        <div class="project-style-create__asset-browser-button-content">
          <Icon
            icon="plm-styles"
            size="24"
          />
          Fill Style Data
        </div>
      </ElButton>
    </ElPopover>
    <ElButton
      slot="header-right"
      :disabled="!styleNameTemporary || optionUploadStatus"
      plain
      type="primary"
      @click="goBack"
    >
      Done
    </ElButton>

    <div
      slot="screen-center"
      class="project-style-create__style-template"
    >
      <div class="project-style-create__files-upload">
        <div class="project-style-create__files-upload-left">
          <StyleOptionController
            controller-title="Options"
            :style-id="styleId"
          />
        </div>
        <div class="project-style-create__files-upload-right">
          <StyleDetailController
            controller-title="Detail images"
            :style-id="styleId"
          />
        </div>
      </div>
      <div class="project-style-create__metadata-menu">
        <StyleTemplateMetadata
          :style-id="styleId"
          @update-style-metadata="updateStyleMetadata"
        />
      </div>
    </div>
  </Screen>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ClickOutside from 'element-ui/src/utils/clickoutside'
import Screen from '@/components/Screen.vue'
import StyleTemplateMetadata from '@/components/project/StyleTemplateMetadata.vue'
import StyleOptionController from '@/components/project/StyleOptionController.vue'
import StyleDetailController from '@/components/project/StyleDetailController.vue'
// projectId
import { Projects } from '@/mixins/projects'
import { Workspace } from '@/mixins/workspace'
import { styleMetaKeys } from '@/constants/meta'
import Utils from '@/services/utils'
import features from '@/constants/features'
import Icon from '@/components/Icon'
import { defaultStyle } from '@/constants/style'

export default {
  name: 'ProjectCreateStyle',

  components: {
    Icon,
    Screen,
    StyleTemplateMetadata,
    StyleOptionController,
    StyleDetailController
  },

  directives: {
    ClickOutside
  },

  mixins: [Projects, Workspace],

  /**
   * @param {object}   to
   * @param {object}   from
   * @param {Function} next
   */
  async beforeRouteLeave (to, from, next) {
    await this.deleteStyleIfNeeded()
    next()
  },

  data: () => ({
    styleId: null,
    styleNameTemporary: null,
    showAssetManager: false,
    features
  }),

  computed: {
    ...mapGetters([
      'getStyleById',
      'getOptionsByStyleId',
      'getDetailsByStyleId',
      'getStyleMetaFilters',
      'getOptionUploadStatus'
    ]),

    /**
     * @returns {object}
     */
    style () {
      if (this.styleId) {
        return this.getStyleById(this.styleId)
      } else {
        return null
      }
    },

    /**
     * @returns {string | null}
     */
    styleName () {
      return this.style ? this.style.name : null
    },

    /**
     * @returns {object}
     */
    styleMetaFilters () {
      return this.getStyleMetaFilters
    },

    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.selectedGroupId)
    },

    /**
     * @returns {string}
     */
    backButtonRoute () {
      return `/projects/${this.projectId}/create`
    },

    /**
     * @returns {boolean}
     */
    canUserAccessFeatureStyleDataIntegration () {
      return this.$featureFlagsManager.canAccessFeature(
        features.STYLE_DATA_INTEGRATION,
        {
          user: this.user
        }
      )
    },

    /**
     * @returns {object}
     */
    optionUploadStatus () {
      return this.getOptionUploadStatus
    }
  },

  created () {
    this.fetchCurrentStyle()
  },

  methods: {
    ...mapActions([
      'fetchStyle',
      'deleteStyle',
      'updateStyle',
      'updateStyleLabel',
      'createStyleCustomTag',
      'deleteStyleCustomTag',
      'updateOption',
      'createStyleMetaFilter',
      'fetchStyleMetaFilters',
      'fetchStyleHubFilters'
    ]),

    /**
     */
    async fetchCurrentStyle () {
      const styleId = parseInt(this.$route.params.styleId)

      if (Number.isInteger(styleId)) {
        this.styleId = styleId

        await this.fetchStyle({
          styleId: this.styleId,
          projectId: this.projectId
        })

        this.styleNameTemporary = this.styleName
      }
    },

    /**
     * @param {object} payload
     */
    async updateStyleMetadata (payload) {
      const {
        styleId,
        newTagText,
        tagIdToRemove,
        name,
        label,
        metaToAssign,
        meta,
        attributes: changedAttributes = {},
        ...stylePayload
      } = payload

      if (name !== undefined && name !== null) {
        this.styleNameTemporary = name
      }

      if (label || label === '') {
        await this.updateStyleLabel({
          styleId,
          label
        })
      }

      if (newTagText) {
        await this.createStyleCustomTag({
          styleId,
          text: newTagText
        })
      }

      if (metaToAssign) {
        await Promise.all(
          Object.entries(metaToAssign).map(([field, value]) =>
            this.createStyleMetaFilter({
              field,
              value,
              styleId
            })
          )
        )

        this.fetchStyleMetaFilters()
      }

      if (tagIdToRemove) {
        await this.deleteStyleCustomTag({
          styleId,
          tagId: tagIdToRemove
        })
      }

      if (
        name ||
        changedAttributes ||
        meta ||
        Object.entries(stylePayload).length > 0
      ) {
        let allAttributesAsArray = changedAttributes

        // Merges objects and removes the ones with null values (meaning they were removed)
        if (this.style) {
          allAttributesAsArray = Object.entries({
            ...this.style.attributes,
            ...changedAttributes
          })
        }

        const attributes = Object.fromEntries(
          allAttributesAsArray.filter(([key, value]) => value !== null)
        )

        this.updateStyle({
          styleId,
          projectId: this.projectId,
          name,
          attributes,
          meta,
          ...stylePayload
        })
      }
    },

    /**
     */
    goBack () {
      this.$router.replace(this.backButtonRoute)
    },

    /**
     */
    async deleteStyleIfNeeded () {
      if (!this.style) {
        return
      }

      if (this.isEmptyStyle(this.style)) {
        await this.deleteStyle({
          styleId: this.style.id
        })
      }
    },

    /**
     * @param   {object}  style
     *
     * @returns {boolean}
     */
    isEmptyStyle (style) {
      const options = this.getOptionsByStyleId(style.id)
      const details = this.getDetailsByStyleId(style.id)
      const isStyleChanged = !Object.entries(defaultStyle).every(
        ([defaultStyleKey, defaultStyleValue]) => {
          return (
            JSON.stringify(style[defaultStyleKey]) ===
            JSON.stringify(defaultStyleValue)
          )
        }
      )

      const hasContent =
        isStyleChanged || options.length > 0 || details.length > 0

      return !hasContent
    },

    /**
     * @param {object} hubStyle
     */
    async handleSelectedStyle (hubStyle) {
      const { name, attributes, price } = hubStyle
      const payload = {
        name,
        metaToAssign: {
          [styleMetaKeys.PRICE_ID]: price
        },
        attributes,
        styleId: this.styleId
      }

      if (this.isEmptyStyle(this.style)) {
        await this.updateStyleMetadata(payload)
      } else {
        this.$confirm(
          'This will overwrite the current style data. Continue?',
          'Warning',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No Way',
            type: 'warning',
            showClose: false
          }
        )
          .then(() => {
            this.updateStyleMetadata(payload)
          }) // Canceling returns a rejected promise
          .catch(() => {})
      }

      this.closeAssetManager()
    },

    /**
     */
    closeAssetManager () {
      this.showAssetManager = false
    }
  }
}
</script>

<style lang="scss" scoped>
$min-width-metadata-menu: 150px;
$width-metadata-menu: 12%;
$width-upload-zone: 50%;
$width-style-template-left: calc(100% - #{$width-metadata-menu});

.project-style-create {
  background-color: $white;
}

.project-style-create__style-template {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
}

.project-style-create__files-upload,
.project-style-create__metadata-menu {
  overflow-y: auto;
  cursor: default;
}

.project-style-create__files-upload {
  display: flex;
  justify-content: space-between;
  width: $width-style-template-left;
  padding-right: $spacer-unit-x2;
}

.project-style-create__files-upload-left,
.project-style-create__files-upload-right {
  width: $width-upload-zone;
}

.project-style-create__files-upload-left {
  padding-right: $spacer-unit;
}

.project-style-create__files-upload-right {
  padding-left: $spacer-unit;
}

.project-style-create__metadata-menu {
  width: $width-metadata-menu;
  min-width: $min-width-metadata-menu;
  padding-left: $spacer-unit-x2;
  border-left: $border-size solid $grey-extra-light;
}

.project-style-create__asset-browser-button {
  /deep/ svg {
    fill: $grey;
  }

  &:hover {
    /deep/ svg {
      fill: $blue;
    }
  }
}

.project-style-create__asset-browser-button-content {
  @include flex-horizontal-space-between;

  pointer-events: none;
}
</style>

<style lang="scss">
.project-style-create__asset-browser-popup {
  padding: 0;
}
</style>
